/**
 * useSpatialNavigation Hook
 *
 * Provides a clean interface for spatial navigation throughout the app.
 * Wraps the spatial store with convenience methods and computed values.
 */

import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useSpatialStore } from '../stores/spatialStore';
import { CampusLocation } from '../types/spatial';
import { getLocationConfig, LOCATION_TO_TAB, TAB_TO_LOCATION } from '../config/locationConfig';

export interface UseSpatialNavigationResult {
  /** Current location */
  currentLocation: CampusLocation;
  /** Previous location (for back navigation) */
  previousLocation: CampusLocation | null;
  /** Whether currently at campus overview */
  isAtCampus: boolean;
  /** Whether a transition animation is in progress */
  isTransitioning: boolean;
  /** List of unlocked locations */
  unlockedLocations: CampusLocation[];
  /** Navigate to a specific location */
  navigateTo: (location: CampusLocation) => void;
  /** Navigate back to previous location */
  goBack: () => void;
  /** Navigate to campus overview */
  goToCampus: () => void;
  /** Check if a location is unlocked */
  isUnlocked: (location: CampusLocation) => boolean;
  /** Get the current location's display name */
  currentLocationName: string;
  /** Get the dashboard tab equivalent for the current location */
  currentTab: string;
  /** Navigate to a location by tab name (for compatibility) */
  navigateToTab: (tab: string) => void;
}

/**
 * Hook providing spatial navigation functionality
 */
export const useSpatialNavigation = (): UseSpatialNavigationResult => {
  // Select state with shallow comparison for performance
  const {
    currentLocation,
    previousLocation,
    isTransitioning,
    unlockedLocations,
    navigateToLocation,
    navigateBack,
  } = useSpatialStore(
    useShallow((state) => ({
      currentLocation: state.currentLocation,
      previousLocation: state.previousLocation,
      isTransitioning: state.isTransitioning,
      unlockedLocations: state.unlockedLocations,
      navigateToLocation: state.navigateToLocation,
      navigateBack: state.navigateBack,
    }))
  );

  // Computed values
  const isAtCampus = currentLocation === CampusLocation.CAMPUS_OVERVIEW;

  const currentLocationName = useMemo(
    () => getLocationConfig(currentLocation).name,
    [currentLocation]
  );

  const currentTab = useMemo(
    () => LOCATION_TO_TAB[currentLocation],
    [currentLocation]
  );

  // Navigation functions
  const navigateTo = useCallback(
    (location: CampusLocation) => {
      navigateToLocation(location);
    },
    [navigateToLocation]
  );

  const goBack = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  const goToCampus = useCallback(() => {
    if (currentLocation !== CampusLocation.CAMPUS_OVERVIEW) {
      navigateToLocation(CampusLocation.CAMPUS_OVERVIEW);
    }
  }, [currentLocation, navigateToLocation]);

  const isUnlocked = useCallback(
    (location: CampusLocation) => unlockedLocations.includes(location),
    [unlockedLocations]
  );

  const navigateToTab = useCallback(
    (tab: string) => {
      const location = TAB_TO_LOCATION[tab];
      if (location) {
        navigateToLocation(location);
      }
    },
    [navigateToLocation]
  );

  return {
    currentLocation,
    previousLocation,
    isAtCampus,
    isTransitioning,
    unlockedLocations,
    navigateTo,
    goBack,
    goToCampus,
    isUnlocked,
    currentLocationName,
    currentTab,
    navigateToTab,
  };
};

/**
 * Hook for building selection state
 */
export const useBuildingSelection = () => {
  return useSpatialStore(
    useShallow((state) => ({
      selectedBuilding: state.selectedBuilding,
      selectBuilding: state.selectBuilding,
      hoverBuilding: state.hoverBuilding,
    }))
  );
};

/**
 * Hook for transition preferences
 */
export const useTransitionPreferences = () => {
  return useSpatialStore(
    useShallow((state) => ({
      preferences: state.transitionPreferences,
      updatePreferences: state.updateTransitionPreferences,
    }))
  );
};

export default useSpatialNavigation;
